export const BRIDGE_SOURCE = 'nova-embed-bridge' as const;
export const PROTOCOL_VERSION = 1 as const;
export const LOADER_VERSION = '1.3.0';

export const HOST_APPS = [
  'accounts',
  'claims',
  'conversion',
  'files',
  'navigator',
  'payments',
  'quoting',
  'trading-partners',
] as const;

export type HostApp = (typeof HOST_APPS)[number];
export type NovaDimension = 'panel' | 'panel-sidebar' | 'fullscreen';
/**
 * `floating`: Nova draws its own launcher and the host sizes the iframe to it (Newfront apps).
 * `docked`: the host gives Nova a side panel; closed, Nova shows a narrow rail instead of the launcher.
 */
export type NovaLayout = 'floating' | 'docked';
/** The width of the docked rail, which a docked host sizes its panel to while Nova is closed. */
export const DOCKED_RAIL_WIDTH = 64;

export interface NovaLauncherSize {
  /** Visible launcher chrome. These dimensions exclude the overflow inset. */
  width: number;
  height: number;
  /** Extra iframe viewport space required for chrome that bleeds beyond the visible launcher. */
  inset?: Readonly<{ top: number; right: number }>;
}

export interface NovaHostContext {
  hostApp: HostApp;
  accountUuid?: string;
}

export interface NovaHostContextPatch {
  accountUuid?: string | null;
}

export interface HostVisibility {
  visible: boolean;
  focused: boolean;
}

export interface NovaBridgeEnvelope<TType extends string, TPayload> {
  source: typeof BRIDGE_SOURCE;
  protocolVersion: number;
  type: TType;
  payload: TPayload;
}

export type NovaReadyMessage = NovaBridgeEnvelope<'nova:ready', { embedVersion: string; capabilities: string[] }>;
export type NovaStateMessage = NovaBridgeEnvelope<
  'nova:state',
  {
    enabled: boolean;
    open: boolean;
    dimension: NovaDimension;
    hasNotifications: boolean;
    launcher?: NovaLauncherSize;
  }
>;
export type NovaLauncherMoveMessage = NovaBridgeEnvelope<'nova:launcher-move', { deltaX: number; persist: boolean }>;
export type NovaErrorMessage = NovaBridgeEnvelope<
  'nova:error',
  { code: 'unauthenticated' | 'internal'; message?: string }
>;

/** The chat the user is on; null for a new chat they haven't sent anything in. Lets the host reopen it later. */
export type NovaChatMessage = NovaBridgeEnvelope<'nova:chat', { chatId: string | null }>;
/** A cross-origin host (e.g. Navigator) supplies Nova's credential; Nova asks again before it expires. */
export type NovaAuthRequestMessage = NovaBridgeEnvelope<'nova:auth-request', Record<string, never>>;

export type HostInitMessage = NovaBridgeEnvelope<
  'host:init',
  {
    loaderVersion: string;
    hostApp: HostApp;
    context: NovaHostContext;
    visibility: HostVisibility;
    open: boolean;
    /** Defaults to `floating`. */
    layout?: NovaLayout;
  }
>;
export type HostContextMessage = NovaBridgeEnvelope<'host:context', { patch: NovaHostContextPatch }>;
export type HostOpenMessage = NovaBridgeEnvelope<'host:open', Record<string, never>>;
export type HostCloseMessage = NovaBridgeEnvelope<'host:close', Record<string, never>>;
export type HostToggleMessage = NovaBridgeEnvelope<'host:toggle', Record<string, never>>;
/** Opens one of the user's chats, e.g. the one Nova last reported with `nova:chat` for this placement. */
export type HostOpenChatMessage = NovaBridgeEnvelope<'host:open-chat', { chatId: string }>;
export type HostVisibilityMessage = NovaBridgeEnvelope<'host:visibility', HostVisibility>;
/** An access token issued to Nova by the host's identity provider (Entra), exchanged by Nova for its own token. */
export type HostAuthMessage = NovaBridgeEnvelope<'host:auth', { accessToken: string }>;

/** Nova rejects larger embed context rather than truncating it; the backend enforces the same limits. */
export const EMBED_CONTEXT_MARKDOWN_MAX_LENGTH = 50_000;
export const EMBED_CONTEXT_METADATA_MAX_LENGTH = 10_000;

/**
 * The host's view for Nova to open new chats on (markdown) and its identifiers for it (e.g. placement IDs,
 * client GCID). A chat's first message stores both with the chat. Sending another starts a new chat.
 */
export type HostEmbedContextMessage = NovaBridgeEnvelope<
  'host:embed-context',
  { markdown: string; metadata: Record<string, unknown> }
>;

export type NovaToHostMessage =
  | NovaReadyMessage
  | NovaStateMessage
  | NovaLauncherMoveMessage
  | NovaErrorMessage
  | NovaAuthRequestMessage
  | NovaChatMessage;
export type HostToNovaMessage =
  | HostInitMessage
  | HostContextMessage
  | HostOpenMessage
  | HostCloseMessage
  | HostToggleMessage
  | HostVisibilityMessage
  | HostAuthMessage
  | HostEmbedContextMessage
  | HostOpenChatMessage;

export type NovaToHostMessageType = NovaToHostMessage['type'];
export type HostToNovaMessageType = HostToNovaMessage['type'];

type NovaBridgeMessage = NovaToHostMessage | HostToNovaMessage;

export function createBridgeMessage<TType extends NovaBridgeMessage['type']>(
  type: TType,
  payload: Extract<NovaBridgeMessage, { type: TType }>['payload'],
): Extract<NovaBridgeMessage, { type: TType }> {
  return {
    source: BRIDGE_SOURCE,
    protocolVersion: PROTOCOL_VERSION,
    type,
    payload,
  } as Extract<NovaBridgeMessage, { type: TType }>;
}
